// ONE nav definition for both layouts.
//
// MainLayout and DetailLayout each used to carry their own copy, and they
// drifted: the detail pages (patient/provider detail) were missing the
// Conversions item entirely and still said "Health Conditions" / "Intake
// Form" where the main nav had been shortened to "Conditions" / "Forms". Any
// new section had to be added in two places or it silently vanished on half
// the app — so the list, the alert hook, and the dot all live here now.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  UsersIcon,
  FireIcon,
  HomeIcon,
  ChartBarIcon,
  UserIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  StatusOnlineIcon,
  ClipboardListIcon,
  TruckIcon,
  MailIcon,
} from "@heroicons/react/outline";
import {
  BASE_DASHBOARD_PATH,
  CONVERSIONS,
  BASE_ADMIN_PATH,
  BASE_PATIENT_PATH,
  BASE_PROVIDER_PATH,
  BASE_MEDICAL_CONDITONS_NAMES,
  INTAKE_FORMS,
  OPERATIONS_DASHBOARD,
  VISIT_TYPES,
  MAIL_ORDER_PHARMACIES,
  EMAILS,
} from "Constants/Views";
import { ReactComponent as HealthConditionIcon } from "Assets/HealthCondition.svg";
import { ReactComponent as IntakeForms } from "Assets/Form.svg";
import { GetPostbackControls } from "Services/API/Conversions.service";

export const NAV_ITEMS = [
  { name: "Dashboard", path: BASE_DASHBOARD_PATH, icon: HomeIcon },
  { name: "Providers", path: BASE_PROVIDER_PATH, icon: FireIcon },
  { name: "Patients", path: BASE_PATIENT_PATH, icon: UsersIcon },
  { name: "Admins", path: BASE_ADMIN_PATH, icon: UserIcon },
  { name: "Conditions", path: BASE_MEDICAL_CONDITONS_NAMES, icon: HealthConditionIcon },
  { name: "Forms", path: INTAKE_FORMS, icon: IntakeForms },
  { name: "Visit Types", path: VISIT_TYPES, icon: ClipboardListIcon },
  { name: "Mail Order", path: MAIL_ORDER_PHARMACIES, icon: TruckIcon },
  { name: "Conversions", path: CONVERSIONS, icon: ChartBarIcon },
  { name: "Operations", path: OPERATIONS_DASHBOARD, icon: StatusOnlineIcon },
  { name: "Emails", path: EMAILS, icon: MailIcon },
];

// Red dot on the Conversions nav item when the latest LIVE postback run
// failed — so a broken nightly job is visible from any admin page. Polled
// lazily; a dry run never raises or clears it.
export function usePostbackAlert() {
  const { data } = useQuery(["postback-controls"], GetPostbackControls, {
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
  return data?.lastLiveRunFailed === true;
}

export const AlertDot = () => (
  <span
    className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
    title="Last postback run failed — check the Postbacks tab"
  />
);

// Sidebar dock (hide/show) — shared so it works on every page, not just the
// wide table pages that happen to use MainLayout. The preference is one
// localStorage key, so docking on Conversions keeps it docked when you open a
// patient (which renders DetailLayout).
export function useSidebarDock() {
  const [sidebarDocked, setSidebarDocked] = useState(() => localStorage.getItem("SIDEBAR_DOCKED") === "true");
  const toggleSidebar = () =>
    setSidebarDocked((docked) => {
      localStorage.setItem("SIDEBAR_DOCKED", String(!docked));
      return !docked;
    });
  return { sidebarDocked, toggleSidebar };
}

export const SidebarDockButton = ({ sidebarDocked, toggleSidebar }) => (
  <button
    type="button"
    onClick={toggleSidebar}
    title={sidebarDocked ? "Show sidebar" : "Hide sidebar"}
    className="hidden lg:flex fixed bottom-4 z-50 items-center justify-center h-8 w-8 rounded-full bg-blue-chill text-white shadow-lg hover:opacity-90 focus:outline-none"
    style={{ left: sidebarDocked ? "10px" : "94px" }}
  >
    {sidebarDocked ? (
      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
    ) : (
      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
    )}
  </button>
);
